import { LoadingOutlined } from "@ant-design/icons";
import { useGetIdentity } from "@refinedev/core";
import {
  Button,
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  message,
  Radio,
  Row,
  Spin,
  Typography
} from "antd";
import { FormInstance } from "antd/lib";
import { updateUserNonAdmin, updateUserPassword } from "api/userData";
import { VALIDATION } from "constants/strings";
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserIdentityData } from "types/props";
import { STRONG_PASSWORD_PATTERN } from "utils/validation";

export const SettingsPage: React.FC = () => {
  const [tabIndex, setTabIndex] = useState(0);

  const { data: user } = useGetIdentity<UserIdentityData>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const informationFormRef = useRef<FormInstance | null>(null);
  const passwordFormRef = useRef<FormInstance | null>(null);

  useEffect(() => {
    const queryParameters = new URLSearchParams(window.location.search);

    const paymentStatus = queryParameters.get("success");

    if (paymentStatus) {
      if (paymentStatus === "true") {
        message.success("Payment successful.");
      }

      if (paymentStatus === "false") {
        message.error("Payment unsuccessful.");
      }

      navigate("/settings", {
        replace: true
      });
    }
  }, [navigate]);

  const handleSubmit = async (values: {
    firstName: string;
    lastName: string;
    marketingEmailPreference: boolean;
  }) => {
    const { firstName, lastName, marketingEmailPreference } = values;
    if (!firstName.trim() || !lastName.trim()) {
      message.error("Please enter first and last name");
      return;
    }
    if (firstName.trim().length > 255 || lastName.trim().length > 255) {
      message.error("The First Name and Last Name must be less than 255 characters");
      return;
    }
    setLoading(true);
    if (user) {
      const res = await updateUserNonAdmin(user.id, {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        marketingEmailPreference
      });
      if (res) {
        // setUser((prev: any) => ({ ...prev, firstName, lastName, marketingEmailPreference }));
      }
    }
    setLoading(false);
  };

  useEffect(() => {
    informationFormRef.current?.setFieldsValue({
      ...user
    });
    passwordFormRef.current?.resetFields();
  }, [tabIndex, user]);

  const submitPassword = async (values: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }) => {
    const { currentPassword, newPassword, confirmPassword } = values;

    if (!currentPassword || !newPassword || !confirmPassword) {
      message.error("Please fill in all fields");
      return;
    }

    if (newPassword !== confirmPassword) {
      message.error("Passwords do not match");
      return;
    }

    if (currentPassword === newPassword) {
      message.error("New password should be different from current password");
      return;
    }

    setLoading(true);
    const res = await updateUserPassword({ currentPassword, newPassword });
    setLoading(false);
    if (res) {
      message.success("Password updated successfully");
      passwordFormRef.current?.resetFields();
    }
  };

  return (
    <Spin
      spinning={!user}
      indicator={<LoadingOutlined style={{ fontSize: 48, color: "white" }} spin />}>
      <div>
        <Row>
          <Typography
            style={{
              fontWeight: 500,
              fontSize: "30px",
              lineHeight: "38px"
            }}>
            Settings
          </Typography>
        </Row>
        <Row>
          <Radio.Group
            style={{ marginBottom: 16, marginTop: 16, display: "flex" }}
            defaultValue={0}
            value={tabIndex}
            onChange={(e) => {
              setTabIndex(e.target.value);
            }}
            disabled={loading}>
            <Radio.Button
              style={{
                width: "98px",
                color: "#CECFD2",
                fontSize: "14px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              value={0}>
              Account
            </Radio.Button>
            <Radio.Button
              style={{
                width: "98px",
                color: "#CECFD2",
                fontSize: "14px",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center"
              }}
              value={1}>
              Password
            </Radio.Button>
          </Radio.Group>
        </Row>
        <Row>
          <Divider style={{ backgroundColor: "#404040", padding: 0, margin: 0 }} />
        </Row>
        <Row>
          <Card
            style={{
              backgroundColor: "#202440",
              borderRadius: "10px",
              width: "100%",
              borderColor: "#202440",
              marginTop: "18px"
            }}>
            <Row>
              <Col span={10}>
                <Typography.Title
                  style={{
                    fontSize: "16px",
                    fontWeight: 600,
                    lineHeight: "23.2px"
                  }}>
                  Personal Information
                </Typography.Title>
                <Typography style={{ fontSize: "14px", fontWeight: 400, color: "#B5B5B5" }}>
                  Update your personal details here.
                </Typography>
                <Button
                  style={{
                    border: "none",
                    marginTop: "12px",
                    height: "44px",
                    fontSize: "14px",
                    lineHeight: "20.3px"
                  }}
                  loading={loading}
                  onClick={() => {
                    if (tabIndex === 0) {
                      informationFormRef.current?.submit();
                    } else {
                      passwordFormRef.current?.submit();
                    }
                  }}>
                  Save Changes
                </Button>
              </Col>
              <Col span={14}>
                {tabIndex === 0 ? (
                  <Form
                    ref={informationFormRef}
                    onFinish={handleSubmit}
                    requiredMark={false}
                    initialValues={{
                      firstName: user?.firstName,
                      lastName: user?.lastName
                    }}>
                    <Form.Item
                      label={"First Name"}
                      labelCol={{
                        span: 24
                      }}
                      colon={false}
                      name="firstName"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: VALIDATION.fieldIsRequired
                        },
                        {
                          min: 2,
                          whitespace: true,
                          transform: (value: string) => value.trim(),
                          message: VALIDATION.getMinCharacterValidationMessage("First name", 2)
                        },
                        {
                          max: 255,
                          message: VALIDATION.getMaxCharacterValidationMessage("First name", 256)
                        }
                      ]}>
                      <Input defaultValue={user?.firstName} disabled={loading} />
                    </Form.Item>
                    <Form.Item
                      label={"Last Name"}
                      labelCol={{
                        span: 24
                      }}
                      colon={false}
                      name={"lastName"}
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: VALIDATION.fieldIsRequired
                        },
                        {
                          min: 2,
                          whitespace: true,
                          transform: (value: string) => value.trim(),
                          message: VALIDATION.getMinCharacterValidationMessage("Last name", 2)
                        },
                        {
                          max: 255,
                          message: VALIDATION.getMaxCharacterValidationMessage("Last name", 256)
                        }
                      ]}>
                      <Input defaultValue={user?.lastName} disabled={loading} />
                    </Form.Item>
                    <Form.Item
                      label={"Email"}
                      labelCol={{
                        span: 24
                      }}
                      colon={false}>
                      <Input disabled value={user?.email || ""} />
                    </Form.Item>
                    <Form.Item
                      label={"Organization name"}
                      labelCol={{
                        span: 24
                      }}
                      colon={false}>
                      <Input disabled value={user?.organization || ""} />
                    </Form.Item>
                    <Form.Item
                      colon={false}
                      name="marketingEmailPreference"
                      valuePropName="checked">
                      <Checkbox>I would like to receive marketing and newsletter emails</Checkbox>
                    </Form.Item>
                  </Form>
                ) : (
                  <Form ref={passwordFormRef} onFinish={submitPassword} requiredMark={false}>
                    <Form.Item
                      label={"Current password"}
                      labelCol={{
                        span: 24
                      }}
                      name="currentPassword"
                      rules={[
                        {
                          required: true,
                          whitespace: true,
                          message: VALIDATION.fieldIsRequired
                        }
                      ]}
                      colon={false}>
                      <Input.Password disabled={loading} />
                    </Form.Item>
                    <Form.Item
                      label={"New password"}
                      labelCol={{
                        span: 24
                      }}
                      name="newPassword"
                      dependencies={["password"]}
                      normalize={(value) => value.trim()}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (getFieldValue("currentPassword") === value) {
                              return Promise.reject(VALIDATION.newPasswordValidation);
                            }

                            if (!STRONG_PASSWORD_PATTERN.test(value)) {
                              return Promise.reject(VALIDATION.passwordValidation);
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}
                      colon={false}>
                      <Input.Password disabled={loading} />
                    </Form.Item>
                    <Form.Item
                      label={"Confirm new password"}
                      labelCol={{
                        span: 24
                      }}
                      name="confirmPassword"
                      dependencies={["newPassword"]}
                      normalize={(value) => value.trim()}
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (getFieldValue("newPassword") !== value) {
                              return Promise.reject(VALIDATION.confirmPasswordValidation);
                            }

                            return Promise.resolve();
                          }
                        })
                      ]}
                      colon={false}>
                      <Input.Password disabled={loading} />
                    </Form.Item>
                  </Form>
                )}
              </Col>
            </Row>
          </Card>
        </Row>
      </div>
    </Spin>
  );
};
